import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { ec as EC } from "elliptic";

import toast from "react-hot-toast";
import { auth, db } from "../config/firebaseConfig";

const UserContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState({});
  const [userData, setUserData] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isUserDataLoading, setIsUserDataLoading] = useState(false);

  const generateWalletAddress = () => {
    // Example implementation using a simple random string generator
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const walletAddressLength = 42; // Ethereum wallet address length
    let walletAddress = "0x";
    for (let i = 0; i < walletAddressLength - 2; i++) {
      walletAddress += characters.charAt(
        Math.floor(Math.random() * characters.length),
      );
    }
    return walletAddress;
  };
  const generatePrivateKey = () => {
    // Use Web Crypto API to generate a Uint8Array of 32 random bytes
    const randomValues = window.crypto.getRandomValues(new Uint8Array(32));
    // Convert each byte to a two-character hexadecimal number and concatenate them
    const privateKey =
      "0x" +
      Array.from(randomValues)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    return privateKey;
  };

  const generatePublicKey = (privateKey) => {
    const ec = new EC("secp256k1");
    const keyPair = ec.keyFromPrivate(privateKey.slice(2), "hex");
    return "0x" + keyPair.getPublic(false, "hex").slice(2);
  };

  const signUp = async (email, password) => {
    try {
      // Generate wallet address, private key, public key
      const walletAddress = generateWalletAddress();
      const privateKey = generatePrivateKey();
      const publicKey = generatePublicKey(privateKey);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const { user } = userCredential;
      // Store only the authenticated user data in Firestore
      await db.collection("users").doc(user.email).set({
        id: user.uid,
        email: user.email,
        isWalletCreated: false,
        walletAddress,
        privateKey,
        publicKey,
        balance: 10,
        joinedAt: new Date(),
        createdAt: new Date(),
      });
      setUser(user);
      return user; // Return only the user object
    } catch (error) {
      console.error(error.message);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      const userCrenditals = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const { user } = userCrenditals;
      setUser(user);
      return user;
    } catch (error) {
      console.error("Error signing in");
      toast.error("Error signing in");
      throw error;
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser?.email) {
        // Ensure currentUser and email are not null
        fetchUserData(currentUser.email);
      } else {
        console.error("No current user or email undefined");
        // Only clear userData on logout/no user, not on every auth state change
        setUserData(null);
        setIsAuthLoading(false);
        setIsUserDataLoading(false);
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const fetchUserData = async (email) => {
    setIsUserDataLoading(true);
    try {
      const userDoc = await db.collection("users").doc(email).get();
      if (userDoc.exists) {
        setUserData(userDoc.data());
      } else {
        console.error("No user data found for email:", email);
        setUserData(null);
      }
    } catch (error) {
      console.error("Error fetching user data:", error.message);
    } finally {
      setIsUserDataLoading(false);
      setIsAuthLoading(false);
    }
  };

  return (
    <UserContext.Provider
      value={{ signUp, user, logout, signIn, userData, setUserData, isAuthLoading, isUserDataLoading }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(UserContext);
};
